import { handleActions } from 'redux-actions';
import cloneDeep from 'lodash/cloneDeep';
import range from 'lodash/range';
import values from 'lodash/values';
import forEach from 'lodash/forEach';
import _get from 'lodash/get';
import _map from 'lodash/map';

import moment from 'moment';
import qs from 'qs';
import { SubmissionError, getFormValues } from 'redux-form';

import checkIsExcludedCountryCode from '../shared/checkIsExcludedCountryCode';
import http from '../shared/http';
import endpoints from '../../config/endpoints';

import intl from '../shared/intl';
import checkPermissions from '../shared/check-permissions';

import { default as createCalendar, getDateRangeFromCalendar } from '../shared/calendar';

const isRocket = process.env.CPA_ENV === 'rocket';

// const localStorageManager = createLocalStorageManager('conversionsApprove');

const calendar = createCalendar({
	namespace: 'conversionsApprove',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

// actions types
const FETCH_DATA_SUCCESS = 'conversions-approve/fetch-data-success';
const FETCH_DATA_ERROR = 'conversions-approve/fetch-data-error';

const TOGGLE_PROGRESS_BAR = 'conversions-approve/toggle-progress-bar';
const GOTO_PAGE_SUCCESS = 'conversions-approve/goto-page-success';

const LOOKUP_SUCCESS = 'conversions-approve/lookup-success';
const LOOKUP_ERROR = 'conversions-approve/lookup-error';

const REQUEST_MANUAL_APPROVE_SUCCESS = 'conversions-approve/request-manual-approve-success';
const REQUEST_MANUAL_APPROVE_ERROR = 'conversions-approve/request-manual-approve-error';

const FETCH_MANUAL_APPROVE_LEADS_SUCCESS = 'conversions-approve/FETCH_MANUAL_APPROVE_LEADS_SUCCESS';

const CLEAR = 'conversions-approve/clear';
const SAVED_FILTERS = 'conversions-approve/saved-filters';

const RESTRICT_NEW_APPROVE = 'conversions-approve/restrict-new-approve';

const FETCH_DAILY_APPROVE_DATA = 'conversions-approve/FETCH_DAILY_APPROVE_DATA';

const TOGGLE_DAILY_APPROVE_POPUP = 'conversions-approve/TOGGLE_DAILY_APPROVE_POPUP';

// permissions
export const allowedPermissions = {
	manual: 'conversions_manual_approve'
};

// initial state
const initialState = {
	data: null,
	calendar: calendar.initialState,
	manualApproveInitialValues: {
		affiliates: null,
		offers: null,
		countries: null,
		percent_to_approve: null
	},
	table: {
		columns: {
			id: {
				index: 'id',
				title: 'ID'
			},
			date: {
				index: 'date',
				title: 'Date'
			},
			affiliate: {
				index: 'affiliate',
				title: 'Affiliate'
			},
			offer: {
				index: 'offer',
				title: 'Offer'
			},
			geoIp: {
				title: 'Geo/IP'
			},
			payout: {
				index: 'payout',
				title: 'Payout'
			},
			campaign: {
				index: 'campaign',
				title: 'Campaign'
			}
		}
	},
	pagination: {
		currentPage: 1,
		perPage: 100,
		total: null
	},
	showProgressBar: false,
	showDailyApprovePopup: false,
	dailyApproveList: [],
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: () => (dispatch, getState) => {
			const savedFilters = null;

			if (document.isLoading) {
				return;
			}

			document.isLoading = true;

			const { pagination: { perPage } } = getState().conversionsApprove;
			request(0, perPage, getState, savedFilters)(dispatch)
				.then(action => {
					document.isLoading = false;
				});

			dispatch(actions.fetchDailyApprove);
		},
		restrictNewApprove: () => (dispatch, getState) => {
			const state = getState();
			let date_range = getDateRangeFromCalendar(state.conversionsApprove.calendar);
			const manualApproveFormData = cloneDeep(getFormValues('manualApproveForm')(state));
			let { offers, countries, affiliates } = manualApproveFormData;

			const country = countries && countries[0] && countries[0].value || [];
			affiliates = affiliates && affiliates.map(i => i.value) || [];
			offers = offers && offers.map(i => i.value) || [];
			date_range = {
				starts_at: moment(date_range.start).format('YYYY-MM-DDTHH:mm:ssZ'),
				ends_at: moment(date_range.end).format('YYYY-MM-DDTHH:mm:ssZ')
			};

			const data = [];

			for (const aff of affiliates) {
				if (!offers.length)
					data.push({ ...date_range, country_id: country, affiliate_id: aff, offer_id: null });
				for (const off of offers) {
					data.push({ ...date_range, country_id: country, affiliate_id: aff, offer_id: off });
				}
			}

			dispatch(actions.toggleProgressBar());

			const request = http({
				url: `${endpoints.CONVERSIONS.BASE}/restrict-new-approve`,
				method: 'post',
				data,
				actions: [ RESTRICT_NEW_APPROVE ]
			});

			return request(dispatch)
				.then(action => {
					if (action.type === RESTRICT_NEW_APPROVE)
						return dispatch(actions.requestManualApprove(manualApproveFormData));
				});
		},
		toggleDailyApprovePopup: (data) => (dispatch, getState) => {
			dispatch({ type: TOGGLE_DAILY_APPROVE_POPUP, payload: data });
		},
		fetchDailyApprove: (isOnAffiliateChange = false) => (dispatch, getState) => {

			const manualApproveFormData = getFormValues('manualApproveForm')(getState());

			const affiliate_id = _get(manualApproveFormData, 'affiliates.0.value');
			const offer_ids = _map(_get(manualApproveFormData, 'offers'), off => off.value);
			const country_ids = _map(_get(manualApproveFormData, 'countries'), off => off.value);

			if (!isOnAffiliateChange) {
				if (!affiliate_id || !(offer_ids && offer_ids[0]) || !(country_ids && country_ids[0])) {
					return;
				}
			} else  {
				if (!affiliate_id) {
					return;
				}
			}

			const request = http({
				url:
					`${endpoints.CONVERSIONS.DAILY_APPROVE}/${affiliate_id}?${qs.stringify({ offer_ids, country_ids })}`,
				method: 'get',
				actions: [ FETCH_DAILY_APPROVE_DATA ]
			});

			return request(dispatch)
				.then(action => {
					if (action.type === FETCH_DAILY_APPROVE_DATA) {
						if (action.payload.data && action.payload.data.length)
							return dispatch(actions.toggleDailyApprovePopup({ dailyApproveList: action.payload.data }));
					}
				});
		},

		locationsLookup: text => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC}?query=${text}`,
				method: 'get',
				actions: [LOOKUP_SUCCESS, LOOKUP_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === LOOKUP_SUCCESS) {
					const data = action.payload.data
						.filter(entity => !(isRocket && checkIsExcludedCountryCode(entity.country_code)));

					return { options: data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},

		affiliatesLookup: text => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.LOOKUPS.AFFILIATES}?query=${text}`,
				method: 'get',
				actions: [LOOKUP_SUCCESS, LOOKUP_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === LOOKUP_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},

		offersLookup: (text, isShowedRemoved = true) => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.LOOKUPS.OFFERS}?${qs.stringify({ query: text, isShowedRemoved })}`,
				method: 'get',
				actions: [LOOKUP_SUCCESS, LOOKUP_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === LOOKUP_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},

		gotoPage: page => (dispatch, getState) => {
			const { pagination: { perPage } } = getState().conversionsApprove;
			const offset = (page - 1) * perPage;

			dispatch(actions.toggleProgressBar());

			request(offset, perPage, getState)(dispatch).then(action => {
				if (action.type === FETCH_DATA_SUCCESS) {
					dispatch({
						type: GOTO_PAGE_SUCCESS,
						payload: page
					});
				}
			});
		},

		requestManualApprove: formData => (dispatch, getState) => {
			const state = getState();
			const userId = state.root.data.id;
			const date_range = getDateRangeFromCalendar(state.conversionsApprove.calendar);

			let { percent_to_approve, offers, countries, affiliates, sid } = cloneDeep(formData);

			const should_add_percent = `${percent_to_approve}`.indexOf('+') !== -1;
			percent_to_approve = parseInt(percent_to_approve);

			const data = {
				date_range,
				percent_to_approve,
				offers: offers && offers.map(i => i.value) || [],
				countries: countries && countries.map(i => i.value) || [],
				affiliates: affiliates && affiliates.map(i => i.value) || [],
				sid,
				should_add_percent
			};

			const approveRequest = http({
				url: `${endpoints.CONVERSIONS.BASE}/manual`,
				method: 'post',
				data,
				actions: [REQUEST_MANUAL_APPROVE_SUCCESS, REQUEST_MANUAL_APPROVE_ERROR],
				addToResponse: { offers, countries, affiliates }
			});

			dispatch(actions.toggleProgressBar());

			return approveRequest(dispatch).then(action => {
				if (action.type === REQUEST_MANUAL_APPROVE_SUCCESS) {
					// localStorageManager.setData(userId)('filters', { offers, countries, affiliates });
					dispatch({ type: SAVED_FILTERS, payload: userId });
					return request(0, state.conversionsApprove.pagination.perPage, getState)(dispatch);
				}

				if (action.type === REQUEST_MANUAL_APPROVE_ERROR && action.payload.response.status === 400) {
					const validationsErrors = {};
					const responseErrors = action.payload.response.data;

					forEach(responseErrors, value => {
						const key = value.dataPath.slice(1);
						validationsErrors[key] = value.message;
					});

					dispatch(actions.toggleProgressBar());

					throw new SubmissionError(validationsErrors);
				}
			});
		},

		getConversionsForApprove: () => (dispatch, getState) => {
			const formData = cloneDeep(getFormValues('manualApproveForm')(getState()));
			const state = getState();
			const date_range = getDateRangeFromCalendar(state.conversionsApprove.calendar);

			let { percent_to_approve, offers, countries, affiliates, sid } = cloneDeep(formData);

			const should_add_percent = `${percent_to_approve}`.indexOf('+') !== -1;
			percent_to_approve = parseInt(percent_to_approve);

			const data = {
				date_range,
				percent_to_approve,
				offers: offers && offers.map(i => i.value) || [],
				countries: countries && countries.map(i => i.value) || [],
				affiliates: affiliates && affiliates.map(i => i.value) || [],
				sid,
				should_add_percent
			};

			const approveRequest = http({
				url: `${endpoints.CONVERSIONS.BASE}/pending-manual?${qs.stringify(data)}`,
				method: 'get',
				actions: [ FETCH_MANUAL_APPROVE_LEADS_SUCCESS ]
			});

			return approveRequest(dispatch)
				.then(action => {
					if (action.type === FETCH_MANUAL_APPROVE_LEADS_SUCCESS) {
						return action.payload.data;
					}
				});
		},

		clear: () => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());
			// const userId = getState().root.data.id;
			// const savedData = null; //localStorageManager.values(userId);
			const savedFilters = null;
			const { pagination: { perPage } } = getState().conversionsApprove;
			dispatch({ type: CLEAR, payload: savedFilters });
			request(0, perPage, getState, savedFilters)(dispatch);
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		})
	},
	calendar.actions
);

// reducers
const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			manualApproveInitialValues: !action.meta
				? state.manualApproveInitialValues
				: {
					...state.manualApproveInitialValues,
					...action.meta
				},
			data: action.payload.data.rows,
			pagination: {
				...state.pagination,
				total: action.payload.data.total
			},
			showProgressBar: false,
			error: false
		}),
		[GOTO_PAGE_SUCCESS]: (state, action) => ({
			...state,
			pagination: {
				...state.pagination,
				currentPage: action.payload
			}
		}),
		[CLEAR]: (state, action) => ({
			...state,
			pagination: initialState.pagination,
			calendar: initialState.calendar,
			manualApproveInitialValues: action.payload || initialState.manualApproveInitialValues
		}),
		[SAVED_FILTERS]: (state, action) => ({
			...state,
			calendar: initialState.calendar,
			manualApproveInitialValues: {
				...initialState.manualApproveInitialValues//,
				// ...localStorageManager.values(action.payload).filters
			}
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[TOGGLE_DAILY_APPROVE_POPUP]: (state, action) => ({
			...state, showDailyApprovePopup: !state.showDailyApprovePopup, ...action.payload
		}),
		[LOOKUP_ERROR]: state => ({ ...state, showProgressBar: false, error: true }),
		[REQUEST_MANUAL_APPROVE_ERROR]: (state, action) => ({
			...state,
			manualApproveInitialValues: action.meta
		}),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[RESTRICT_NEW_APPROVE]: state => ({ ...state, showProgressBar: false })
	},
	calendar.reducers
);

// reducers
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.conversionsApprove.data,
	getShowDailyApprovePopup: state => state.conversionsApprove.showDailyApprovePopup,
	getDailyApproveList: state => state.conversionsApprove.dailyApproveList,
	getCalendar: state =>
		calendar.selectors.getCalendar(state => state.conversionsApprove.calendar, state => state)(state),
	getPagination: state => {
		const { perPage, total, currentPage } = state.conversionsApprove.pagination;
		return {
			pages: range(Math.ceil(total / perPage)),
			currentPage
		};
	},
	getTable: state => {
		const { table, data } = state.conversionsApprove;
		const rows = state.conversionsApprove.data ? data : [];
		return {
			headers: values(table.columns),
			rows: rows.slice(0, table.rowsNumber),
			sortedColumn: table.sortByColumn,
			rowsNumber: table.rowsNumber,
			totalRows: rows.length
		};
	},
	getManualApproveInitialValues: state => state.conversionsApprove.manualApproveInitialValues,
	getIntl: state => intl(state),
	getProgressBar: state => state.conversionsApprove.showProgressBar,
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.conversionsApprove.error
};

const request = (offset = 0, limit = 100, getState, savedFilters = null) => {
	const query = qs.stringify({
		limit,
		offset
	});

	return http({
		url: `${endpoints.CONVERSIONS.BASE}/manual?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
		addToResponse: savedFilters
	});
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/conversions-approve/index.js